import { MarketingLayout, Hero, BenefitCards, Steps, Faq, FinalCta, Section } from "../components/MarketingLayout";
import { useSeo } from "../lib/useSeo";

export default function GpPractices() {
  useSeo({
    title: "SimplyRefer for GP Practices | Find Psychologists & Psychiatrists",
    description:
      "SimplyRefer helps Australian GP practices find psychologists and psychiatrists by specialty, expertise and availability. Free for GP practices, with no patient information stored.",
    canonicalPath: "/gp-practices",
    ogTitle: "SimplyRefer for GP Practices | Find Psychologists & Psychiatrists",
    ogDescription:
      "Free for GP practices. Search psychologists and psychiatrists by specialty, expertise, availability and current referral status.",
  });

  return (
    <MarketingLayout active="/gp-practices">
      <Hero
        eyebrow="For GP Practices"
        h1="Help your practice refer with more confidence."
        sub="SimplyRefer helps GP practices find psychologists and psychiatrists by specialty, expertise, availability and current referral status, while keeping the referral process familiar."
        primary={{ to: "/signup/gp", label: "Get early GP practice access" }}
        secondary={{ to: "/contact", label: "Contact SimplyRefer" }}
      />

      <BenefitCards items={[
        { title: "Free for GP practices", desc: "SimplyRefer is free for GP practices, with no practitioner subscription or added platform cost." },
        { title: "Know availability before referring", desc: "See which clinicians are accepting new patients, waitlisted or not currently taking referrals." },
        { title: "Search by specialty and expertise", desc: "Filter by clinical focus, expertise and referral fit before sending a referral." },
        { title: "AHPRA-verified clinicians", desc: "Every listed clinician is checked before appearing in the directory." },
        { title: "No patient information stored", desc: "GPs continue using normal referral letters. SimplyRefer does not store patient information." },
        { title: "Clinicians see who's referring", desc: "Optional Insights show clinicians which practices are sending suitable referrals, helping them prioritise reliable sources, without any patient information." },
      ]} />

      <Steps steps={[
        "Search for a suitable psychologist or psychiatrist.",
        "Check specialty, expertise, availability and referral status.",
        "Choose the clinician, then select one of three clear referral options.",
        "Select for Referral records that you referred through your usual system without sharing patient information.",
        "Create a SimplyRefer Reference to copy into your usual referral, or Generate Email to open a prefilled email with the clinician's details and reference included.",
        "Completing an option shows clinicians that GPs use their availability information and encourages them to keep availability and wait times current.",
      ]} />

      <Section>
        <p>No patient information is stored in SimplyRefer. Practitioners are prompted monthly to refresh their availability and accepting-new-patients status, helping GP practices reduce dead-ends and returned referrals.</p>
      </Section>

      <Faq items={[
        { q: "Is SimplyRefer free for GP practices?", a: "Yes. SimplyRefer is free for GP practices, with no practitioner subscription or added platform cost. Selected GP practices are being onboarded first during the rollout." },
        { q: "Does SimplyRefer replace our referral process?", a: "No. Choose Select for Referral and continue using your usual referral process or system, or use the optional prefilled email or SimplyRefer Reference." },
        { q: "Does SimplyRefer store patient information?", a: "No. SimplyRefer records only the referral option and referring practice. Patient information stays in your usual clinical system." },
        { q: "What is a SimplyRefer Reference?", a: "It is an optional reference number you can copy into your usual referral so the clinician can confirm it came through SimplyRefer." },
      ]} />

      <FinalCta
        heading="Get early GP practice access."
        primary={{ to: "/signup/gp", label: "Get early GP practice access" }}
        secondary={{ to: "/contact", label: "Contact SimplyRefer" }}
      />
    </MarketingLayout>
  );
}
